"use client"

import React, {useEffect, useState} from "react";

import Image from "next/image";

import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTAButton} from "@/shadcn/ui/button";
import {Carousel, CarouselContent, CarouselItem, type CarouselApi} from "@/shadcn/ui/carousel";
import {motion} from "framer-motion";

import {PageIQCapitalLaunchSchema} from "@/sanity/pages/PageIQCapitalLaunchTemplate";
import {GROQImage} from "@/sanity/lib/types";
import {PortableTextBlock} from "sanity";

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeInDelay = (delay: number) => ({
  ...fadeIn,
  transition: { ...fadeIn.transition, delay }
});

const fadeInStagger = (index: number, baseDelay = 0.1) => ({
  ...fadeIn,
  transition: { ...fadeIn.transition, delay: baseDelay + (index * 0.1) }
});

export default function ExpectSection({
  data,
  className
}: {
  data: PageIQCapitalLaunchSchema;
  className?: string;
}) {

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const Slide = ({item}: {item: {image: GROQImage; text: PortableTextBlock[]}}) => (
    <div className="w-full lg:w-[58.375rem] h-[57.125rem] sm:h-[71.75rem] bg-white/5 gradient-border-white-2 before:opacity-10 relative rounded-[2.5rem] p-[.75rem] overflow-clip">
      <div className="gradient-border-white-2 before:opacity-10 rounded-[1.75rem] relative overflow-clip h-full flex flex-col justify-end ">
        {item?.image && (
          <Image
            className="!w-full !h-full object-cover object-top inset-0 absolute pointer-events-none"
            src={item?.image}
            alt=""
          />
        )}

        {item?.text && (
          <div className="px-[3rem] pb-[5rem] lg:pb-[3rem] richtext richtext-start prose-h3:text-[3rem] prose-h3:mb-[.5rem] prose-h3:text-white/90 prose-h3:font-medium prose-p:text-white/60 prose-p:mb-[1rem] prose-li:my-0 prose-li:text-white/60 relative">
            <PortableText
              value={item.text}
              components={richTextComponent}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <section className={cn(
      "relative pb-[12rem] lg:pb-[25rem]",
      className
    )}>
      <div className="container flex flex-col items-center">
        <div className="w-[min(179rem,100%)]">
          <div className="flex justify-center lg:justify-between items-end mb-[2rem] lg:mb-[4rem]">
            {data?.expectTitle && (
              <motion.div
                {...fadeIn}
                className="richtext lg:richtext-start"
              >
                <PortableText
                  value={data?.expectTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            <motion.div
              {...fadeInDelay(0.1)}
              className="hidden lg:flex gap-[.75rem]"
            >
              <button
                onClick={() => api?.scrollPrev()}
                className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-white/5 transition-all text-white disabled:cursor-not-allowed"
                disabled={!api || current === 0}
                aria-label="Previous slide"
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="rotate-180 w-[3rem] h-auto">
                  <path d="M16.5002 12L9.0002 19.5L7.9502 18.45L14.4002 12L7.9502 5.55L9.0002 4.5L16.5002 12Z" fill="white"/>
                </svg>
              </button>

              <button
                onClick={() => api?.scrollNext()}
                className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-white/5 transition-all text-white disabled:cursor-not-allowed"
                disabled={!api || current === data?.expectItems?.length - 1}
                aria-label="Next slide"
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[3rem] h-auto">
                  <path d="M16.5002 12L9.0002 19.5L7.9502 18.45L14.4002 12L7.9502 5.55L9.0002 4.5L16.5002 12Z" fill="white"/>
                </svg>
              </button>
            </motion.div>
          </div>

          <div className="flex lg:hidden flex-col items-center gap-[3rem]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[2rem]">
              {data?.expectItems?.map((item, index) => (
                <motion.div
                  key={index}
                  {...fadeInStagger(index, 0.1)}
                >
                  <Slide
                    item={item}
                  />
                </motion.div>
              ))}
            </div>

            {data?.expectCTA && (
              <motion.div {...fadeInDelay(0.3)}>
                <CTAButton
                  data={data.expectCTA}
                />
              </motion.div>
            )}
          </div>

          <Carousel
            className="w-full [&_>_div]:!overflow-visible hidden lg:block"
            setApi={setApi}
            opts={{
              align: "start",
              skipSnaps: false,
            }}
          >
            <CarouselContent className={cn(
              "-ml-[2rem] lg:justify-start w-[min(179rem,100%)]",
            )}>
              {data?.expectItems?.map((item, index) => (
                <CarouselItem
                  key={index}
                  className="pl-[2rem] flex"
                >
                  <Slide
                    item={item}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>
  );
}
